import { useState, useEffect, useCallback } from "react";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { List as ListIcon, Calendar as CalIcon, Timer, TrendingUp, Columns, CheckCheck } from "lucide-react";
import { toast } from "sonner";
import { apiFetch } from "@/lib/api";
import { EmptyState, LoadingState } from "@/components/states";
import { usePermissions } from "@/hooks/usePermissions";
import { cn } from "@/lib/utils";
import { CalendarView } from "@/components/tasks/CalendarView";
import { MyTimeView } from "@/components/tasks/MyTimeView";
import { MyProgressView } from "@/components/tasks/MyProgressView";
import { TodayCommand } from "@/components/tasks/TodayCommand";
import { TaskListPremium } from "@/components/tasks/TaskListPremium";
import { KanbanBoard } from "@/components/tasks/KanbanBoard";
import { TaskDrawer } from "@/components/tasks/TaskDrawer";
import { TaskView } from "@/components/tasks/TaskView";
import { TaskQuickCreate } from "@/components/tasks/TaskQuickCreate";
import { BoardManager, BoardActions } from "@/components/tasks/BoardControls";
import { TaskViewControls } from "@/components/tasks/TaskViewControls";
import { useTaskViewPrefs } from "@/hooks/useTaskViewPrefs";
import { AppIcon } from "@/lib/appearanceIcons";

// WHAT (left sidebar / :section) vs HOW (top switcher / ?view). Both operate on
// the same canonical task records. Today & Inbox are their own layouts.
const RECORD_SECTIONS = ["assigned", "personal", "reminders", "waiting", "completed"];
const SECTION_TITLE = {
  today: "Today", inbox: "Inbox", assigned: "Assigned to Me", personal: "Personal",
  reminders: "Reminders", waiting: "Waiting / Blocked", completed: "Completed", boards: "Boards",
};
const HOW = [["list", "List", ListIcon], ["calendar", "Calendar", CalIcon], ["time", "Time", Timer], ["progress", "Progress", TrendingUp]];

export default function MyTasks() {
  const perms = usePermissions();
  const { section = "today" } = useParams();
  const [sp, setSp] = useSearchParams();
  const navigate = useNavigate();
  const view = sp.get("view") || "list";
  const [tasks, setTasks] = useState([]);
  const [cats, setCats] = useState([]);
  const [boards, setBoards] = useState([]);
  const [boardId, setBoardId] = useState("");
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [metaLoaded, setMetaLoaded] = useState(false);
  const [drawer, setDrawer] = useState(null);
  const [viewer, setViewer] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [boardTasksLoaded, setBoardTasksLoaded] = useState(false);
  const canAssign = perms.has("Assign Task");
  // Context-aware create defaults. Header creation is never board-scoped (board
  // columns pass their own board_id/stage), so stale board context can't leak in.
  const createDefaults = section === "today"
    ? { task_type: "personal", due_date: new Date().toISOString().slice(0, 10) }
    : { task_type: "personal" };

  const catName = (id) => cats.find((c) => c.id === id)?.name || "";
  const bump = () => setRefreshKey((k) => k + 1);
  // Keep the boards cache fresh after an optimistic Kanban stage mutation —
  // update in place (no refetch) so the board never flickers or races the DB.
  const onStagesPersist = (newStages) => setBoards((prev) => prev.map((b) => (b.id === boardId ? { ...b, stages: newStages } : b)));

  const loadMeta = useCallback(async () => {
    try {
      const [c, b] = await Promise.all([apiFetch("/tasks/categories").catch(() => []), apiFetch("/tasks/boards").catch(() => [])]);
      setCats(c); setBoards(b);
      if (canAssign) apiFetch("/members").then((m) => setMembers((m || []).filter((x) => x.status === "Active"))).catch(() => {});
    } finally { setMetaLoaded(true); }
  }, [canAssign]);
  useEffect(() => { loadMeta(); }, [loadMeta]);
  useEffect(() => { if (boards.length && !boardId) setBoardId(boards[0].id); }, [boards, boardId]);
  useEffect(() => { setBoardTasksLoaded(false); }, [boardId]);

  const needsTasks = section === "boards" || (RECORD_SECTIONS.includes(section) && view === "list") || section === "inbox";
  const load = useCallback(async () => {
    if (!needsTasks || (section === "boards" && !boardId)) { setLoading(false); return; }
    setLoading(true);
    try {
      const qs = section === "boards" ? `board&board_id=${boardId}` : section === "today" ? "all" : section;
      setTasks(await apiFetch(`/tasks?view=${qs}`));
    } catch (e) { toast.error(e.message); } finally { setLoading(false); if (section === "boards") setBoardTasksLoaded(true); }
  }, [section, view, boardId, needsTasks]);
  useEffect(() => { load(); }, [load, refreshKey]);

  useEffect(() => {
    const n = sp.get("new");
    if (n) { setSp((p) => { p.delete("new"); return p; }, { replace: true }); }
  }, [sp, setSp]);

  const complete = async (t) => {
    const wasDone = t.status === "done";
    setTasks((prev) => prev.filter((x) => x.id !== t.id));
    try {
      await apiFetch(`/tasks/${t.id}/${wasDone ? "reopen" : "complete"}`, { method: "PUT" });
      if (!wasDone) toast.success("Task completed", { action: { label: "Undo", onClick: async () => {
        try { await apiFetch(`/tasks/${t.id}/reopen`, { method: "PUT" }); bump(); } catch (e) { toast.error(e.message); }
      } } });
      bump();
    } catch (e) { toast.error(e.message); load(); }
  };
  const triage = async (t, action) => {
    setTasks((prev) => prev.filter((x) => x.id !== t.id));
    try { await apiFetch(`/tasks/${t.id}/triage`, { method: "PUT", body: JSON.stringify({ action }) }); bump(); }
    catch (e) { toast.error("Could not update the inbox item", { description: e.message }); load(); }
  };

  const board = boards.find((b) => b.id === boardId);
  const showSwitcher = RECORD_SECTIONS.includes(section);
  const setView = (v) => setSp((p) => { p.set("view", v); return p; }, { replace: true });
  const listCtx = section === "boards" ? "board" : section;
  const viewKey = `${section}:${section === "boards" ? "board" : view || "list"}`;
  const { cfg, update: updateCfg } = useTaskViewPrefs(viewKey, listCtx);
  const showDisplayCtl = section === "boards" || (showSwitcher && view === "list") || section === "inbox";

  return (
    <div className="animate-fade-up" data-testid="my-tasks">
      <PageHeader eyebrow="Personal" title={SECTION_TITLE[section] || "My Tasks"}
        subtitle={section === "today" ? "Your calm personal command centre." : section === "inbox" ? "Triage what's come in — nothing gets lost." : "Your work, your way."}
        actions={
          <TaskQuickCreate variant="split" align="end" defaults={createDefaults} members={members} cats={cats} boards={boards} canAssign={canAssign} permsLoading={perms.loading} onCreated={() => { bump(); loadMeta(); }} />
        } />

      {/* HOW switcher — only for record-set sections */}
      {showSwitcher && (
        <div className="mb-6 flex flex-wrap items-center gap-1 border-b border-border/60 pb-3" data-testid="task-primary-views">
          {HOW.map(([k, label, Icon]) => (
            <button key={k} onClick={() => setView(k)} data-testid={`primary-${k}`}
              className={cn("flex items-center gap-2 rounded-lg border px-3 py-1.5 text-xs font-medium transition-colors",
                view === k ? "border-border/80 bg-secondary text-secondary-foreground shadow-soft" : "border-transparent text-muted-foreground hover:bg-muted/60 hover:text-foreground")}>
              <Icon className="h-3.5 w-3.5" /> {label}
            </button>
          ))}
          {showDisplayCtl && view === "list" && <div className="ml-auto"><TaskViewControls cfg={cfg} update={updateCfg} ctx={listCtx} /></div>}
        </div>
      )}

      {section === "boards" && (
        <div className="mb-4 flex flex-wrap items-center gap-2" data-testid="board-bar">
          <Select value={boardId || "none"} onValueChange={(v) => setBoardId(v === "none" ? "" : v)}>
            <SelectTrigger className="h-9 w-56" data-testid="board-select"><Columns className="mr-1 h-3.5 w-3.5" /><SelectValue placeholder="Choose a board" /></SelectTrigger>
            <SelectContent><SelectItem value="none">Choose a board…</SelectItem>{boards.map((b) => <SelectItem key={b.id} value={b.id}><span className="inline-flex items-center gap-2">{b.icon && <AppIcon name={b.icon} className="h-3.5 w-3.5" style={b.accent ? { color: b.accent } : undefined} />}{b.name}</span></SelectItem>)}</SelectContent>
          </Select>
          <BoardManager onChanged={loadMeta} />
          {board && <BoardActions board={board} onChanged={loadMeta} onArchived={() => { setBoardId(""); loadMeta(); }} />}
          <div className="ml-auto"><TaskViewControls cfg={cfg} update={updateCfg} ctx="board" /></div>
        </div>
      )}

      {/* bodies */}
      {section === "today" ? (
        <TodayCommand catName={catName} onOpen={setViewer} onComplete={complete} refreshKey={refreshKey} />
      ) : section === "boards" ? (
        !metaLoaded ? <LoadingState label="Loading boards…" />
          : boards.length === 0 ? <EmptyState title="Create your first board" message="Boards let you organise tasks into your own stages — like To Do → Doing → Done." action={<BoardManager onChanged={loadMeta} inline />} />
          : !board ? <LoadingState label="Loading board…" />
          : !boardTasksLoaded ? <LoadingState label="Loading board…" />
          : <KanbanBoard board={board} tasks={tasks} onOpen={setViewer} onStagesPersist={onStagesPersist} members={members} cats={cats} boards={boards} canAssign={canAssign} cfg={cfg} catName={catName} onChanged={bump} />
      ) : showSwitcher && view === "calendar" ? <CalendarView onReloadCounts={bump} />
        : showSwitcher && view === "time" ? <MyTimeView />
        : showSwitcher && view === "progress" ? <MyProgressView />
        : loading ? <LoadingState label="Loading tasks…" />
        : tasks.length === 0 ? (
          section === "inbox"
            ? <EmptyState title="You're all caught up" message="No un-triaged tasks in your Inbox. Incoming assignments and reminders will appear here." />
            : <EmptyState title="Nothing here yet" message="Create a task to get started — it'll show up across your views automatically." action={<TaskQuickCreate members={members} cats={cats} boards={boards} canAssign={canAssign} onCreated={bump} trigger={<Button size="sm">Add a task</Button>} />} />
        ) : section === "inbox" ? (
          <div className="space-y-2" data-testid="inbox-list">
            {tasks.filter((t) => t.in_inbox).length === 0
              ? <EmptyState title="You're all caught up" message="No un-triaged tasks in your Inbox." />
              : tasks.filter((t) => t.in_inbox).map((t) => (
                <div key={t.id} className="flex items-center gap-3 rounded-xl border border-border/80 bg-card p-4 shadow-soft" data-testid={`inbox-item-${t.id}`}>
                  <button onClick={() => setViewer(t.id)} className="min-w-0 flex-1 text-left">
                    <div className="truncate text-sm font-medium text-foreground">{t.title}</div>
                    <div className="mt-0.5 flex flex-wrap gap-2 text-[11px] text-muted-foreground">
                      <span className="rounded bg-accent-subtle px-1.5 text-accent">{t.task_type === "assigned" ? "Assigned to you" : "Awaiting triage"}</span>
                      {t.due_date && <span>{t.due_date}</span>}
                    </div>
                  </button>
                  <button onClick={() => triage(t, "accept")} data-testid={`triage-accept-${t.id}`} className="inline-flex items-center gap-1 rounded-full border border-border px-2.5 py-1 text-[11px] text-muted-foreground hover:text-foreground"><CheckCheck className="h-3 w-3" /> Accept</button>
                  <button onClick={() => triage(t, "dismiss")} data-testid={`triage-dismiss-${t.id}`} className="rounded-full border border-border px-2.5 py-1 text-[11px] text-muted-foreground hover:text-foreground">Dismiss</button>
                </div>
              ))}
          </div>
        ) : (
          <TaskListPremium tasks={tasks} ctx={listCtx} cfg={cfg} catName={catName} cats={cats} members={members} onOpen={setViewer} onComplete={complete} onChanged={bump} />
        )}

      {viewer && <TaskView taskId={viewer} cats={cats} boards={boards} members={members} canAssign={canAssign}
        onClose={() => setViewer(null)} onEdit={() => { setDrawer(viewer); setViewer(null); }} onChanged={bump} />}
      {drawer && <TaskDrawer taskId={drawer} cats={cats} boards={boards} members={members} canAssign={canAssign} onClose={() => setDrawer(null)} onChanged={bump} />}
    </div>
  );
}
